/**
 * Application shell and routing.
 *
 * Navigation is filtered by the caller's permissions, but that is presentation
 * only: the server authorises every request regardless of what is rendered
 * here (UI-011, RBAC-006). Hiding a link is a courtesy, not a control.
 */

import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import type { Permission } from '@lto/contracts';

import { useSession } from './lib/session';
import { Button, Loading } from './components/ui';
import { TricolourRule, Wordmark } from './components/Brand';
import { NotificationBell } from './components/NotificationBell';
import { SignIn } from './pages/SignIn';
import { Dashboard } from './pages/Dashboard';
import { Confirmations } from './pages/Confirmations';
import { ChangePassword } from './pages/ChangePassword';
import { Compose } from './pages/Compose';
import { ConfirmationDetail } from './pages/ConfirmationDetail';
import { Reports } from './pages/Reports';
import { AuditLog } from './pages/AuditLog';
import { Administration } from './pages/Administration';

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly permission: Permission;
}

/**
 * Ordered as §18 orders it: daily work first, then analysis, then
 * administration. A role holding none of a section's permissions never sees
 * its link.
 */
const NAV: readonly NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', permission: 'dashboard.view' },
  { to: '/confirmations', label: 'Confirmations', permission: 'confirmation.read' },
  { to: '/reports', label: 'Reports', permission: 'report.view' },
  { to: '/administration', label: 'Administration', permission: 'user.read' },
  { to: '/audit', label: 'Audit', permission: 'audit.read' },
];

function Shell() {
  const { user, signOut, can } = useSession();
  const visible = NAV.filter((item) => can(item.permission));

  return (
    <div className="min-h-screen">
      {/* Sticky so navigation stays reachable down a long list. */}
      <header
        className="sticky top-0 z-10 border-b"
        style={{
          background: 'var(--color-surface)',
          borderColor: 'var(--color-rule)',
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        <TricolourRule />
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2.5">
          <Wordmark compact />

          <nav aria-label="Main" className="flex flex-wrap gap-x-4">
            {visible.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className="interactive border-b-2 py-2.5 text-sm font-medium"
                style={({ isActive }) => ({
                  color: isActive ? 'var(--color-accent)' : 'var(--color-ink-muted)',
                  borderColor: isActive ? 'var(--color-accent)' : 'transparent',
                })}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <NotificationBell />
            {user !== null && (
              <span className="text-sm" style={{ color: 'var(--color-ink-subtle)' }}>
                {user.firstName !== '' ? `${user.firstName} ${user.lastName}` : user.email}
                <span className="ml-2 text-xs uppercase tracking-wide">{user.role.replace('_', ' ')}</span>
              </span>
            )}
            <Button onClick={() => void signOut()}>Sign out</Button>
          </div>
        </div>
      </header>

      <main className="animate-fade mx-auto max-w-[1400px] px-4 py-5">
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/confirmations" element={<Confirmations />} />
          <Route path="/confirmations/new" element={<Compose />} />
          <Route path="/confirmations/:id" element={<ConfirmationDetail />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/administration" element={<Administration />} />
          <Route path="/audit" element={<AuditLog />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export function App() {
  const { status, user } = useSession();

  // Restoring the session from the refresh cookie. Rendering the sign-in form
  // during this window would flash it at users who are, in fact, signed in.
  if (status === 'restoring') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loading label="Restoring your session" />
      </div>
    );
  }

  if (status !== 'authenticated') return <SignIn />;

  /**
   * An account still holding an admin-issued credential can do nothing else —
   * the server rejects every guarded route with PASSWORD_CHANGE_REQUIRED. The
   * shell would render a UI where every action fails, so it is bypassed
   * entirely (AUTH-006).
   */
  if (user?.mustChangePassword === true) return <ChangePassword />;

  return <Shell />;
}
